import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { loadJuniorRoster } from "../../lib/mentorRoster";
import JuniorDrawer from "./JuniorDrawer";

const ToolBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 20px;
`;

const Chip = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: ${({ active }) => (active ? "var(--nd-text-display)" : "transparent")};
  color: ${({ active }) => (active ? "var(--nd-black)" : "var(--nd-text-primary)")};
  border: 1px solid var(--nd-border-visible);
  border-radius: 999px;
  padding: 6px 14px;
  cursor: pointer;
`;

const Search = styled.input`
  flex: 1;
  min-width: 200px;
  background: transparent;
  color: var(--nd-text-primary);
  border: 1px solid var(--nd-border);
  border-radius: 4px;
  padding: 8px 12px;
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  &:focus { outline: none; border-color: var(--nd-text-display); }
`;

const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    margin: 0 -16px;
    padding: 0 16px;
  }
`;

const Table = styled.table`
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;
  & th, & td {
    text-align: left;
    padding: 12px;
    border-bottom: 1px solid var(--nd-border);
    font-size: 13px;
    vertical-align: middle;
    white-space: nowrap;
  }
  & th {
    color: var(--nd-text-disabled);
    font-family: 'Space Mono', monospace;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
`;

const SortBtn = styled.button`
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
  font: inherit;
  color: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  &:hover { color: var(--nd-text-display); }
  & .arrow {
    font-size: 9px;
    opacity: ${({ active }) => (active ? 1 : 0)};
    color: var(--nd-text-display);
  }
`;

const OpenBtn = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-primary);
  background: transparent;
  border: 1px solid var(--nd-border-visible);
  border-radius: 999px;
  padding: 4px 12px;
  cursor: pointer;
  &:hover { border-color: var(--nd-text-display); }
`;

const ProgressBar = styled.div`
  position: relative;
  width: 80px;
  height: 4px;
  background: var(--nd-border-visible);
  border-radius: 2px;
  margin-top: 4px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: ${({ pct }) => pct}%;
  background: var(--nd-text-display);
  border-radius: 2px;
`;

const Empty = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  text-align: center;
  margin: 64px 0;
`;

const SORTABLE_COLS = [
  { key: "displayName", label: "Junior", numeric: false },
  { key: "pct", label: "Progress", numeric: true },
  { key: "level", label: "Level", numeric: true },
  { key: "xp", label: "XP", numeric: true },
  { key: "streakDays", label: "Streak", numeric: true },
  { key: "mentorQsToday", label: "Mentor Qs", numeric: true },
];

const JuniorsTab = ({ focusJuniorId, onFocusConsumed }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [atRisk, setAtRisk] = useState(false);
  const [selectedUid, setSelectedUid] = useState(null);
  const [sortBy, setSortBy] = useState("displayName");
  const [sortDir, setSortDir] = useState("asc");

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      const col = SORTABLE_COLS.find((c) => c.key === key);
      setSortDir(col && col.numeric ? "desc" : "asc");
    }
  };

  useEffect(() => {
    loadJuniorRoster().then((data) => {
      setRows(data);
      setLoading(false);
    }).catch((e) => {
      setLoading(false);
      setError((e && e.message) || "failed to load");
    });
  }, []);

  // Deep-link: open drawer for a specific junior
  useEffect(() => {
    if (!focusJuniorId) return;
    setSelectedUid(focusJuniorId);
    if (onFocusConsumed) onFocusConsumed();
  }, [focusJuniorId, onFocusConsumed]);

  const sortVal = (r) => {
    const col = SORTABLE_COLS.find((c) => c.key === sortBy);
    if (!col) return 0;
    if (!col.numeric) return (r[sortBy] || "").toLowerCase();
    return typeof r[sortBy] === "number" ? r[sortBy] : 0;
  };

  const filtered = rows
    .filter((r) => {
      const matchSearch = !search || r.displayName.toLowerCase().includes(search.toLowerCase());
      const matchRisk = !atRisk || r.streakDays === 0 || r.pct < 20;
      return matchSearch && matchRisk;
    })
    .sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      const va = sortVal(a);
      const vb = sortVal(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });

  return (
    <>
      <ToolBar>
        <Chip
          type="button"
          active={atRisk}
          onClick={() => setAtRisk((v) => !v)}
        >
          At-risk
        </Chip>
        <Search
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search junior"
        />
      </ToolBar>
      {loading && <Empty>loading…</Empty>}
      {!loading && error && <Empty>{error}</Empty>}
      {!loading && !error && filtered.length === 0 && <Empty>no juniors</Empty>}
      {!loading && !error && filtered.length > 0 && (
        <TableScroll>
          <Table>
            <thead>
              <tr>
                {SORTABLE_COLS.map((col) => (
                  <th key={col.key}>
                    <SortBtn active={sortBy === col.key} onClick={() => toggleSort(col.key)}>
                      {col.label}{" "}
                      <span className="arrow">
                        {sortBy === col.key && sortDir === "asc" ? "▲" : "▼"}
                      </span>
                    </SortBtn>
                  </th>
                ))}
                <th>Path</th>
                <th>action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.uid}>
                  <td>{r.displayName || "—"}</td>
                  <td>
                    {r.pct}%
                    <ProgressBar>
                      <ProgressFill pct={r.pct} />
                    </ProgressBar>
                  </td>
                  <td>{r.level}</td>
                  <td>{r.xp}</td>
                  <td>{r.streakDays}d</td>
                  <td>{r.mentorQsToday}</td>
                  <td>{r.pathTitle || "—"}</td>
                  <td>
                    <OpenBtn type="button" onClick={() => setSelectedUid(r.uid)}>
                      Open
                    </OpenBtn>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableScroll>
      )}
      {selectedUid && (
        <JuniorDrawer uid={selectedUid} onClose={() => setSelectedUid(null)} />
      )}
    </>
  );
};

export default JuniorsTab;
